import { writable, get } from 'svelte/store';
import type { Category } from '../api';
import { api } from '../api';
import { locale } from 'svelte-i18n';
import { ApiError } from '../api/errorHandler';

interface CategoriesStore {
  subscribe: (fn: (value: Category[]) => void) => () => void;
  load: () => Promise<void>;
  ensureLoaded: () => Promise<void>;
  getLocalizedCategoryName: (
    categoryTechnicalName: string,
    currentLocale?: string | null,
  ) => string;
  getLocalizedSubCategoryName: (
    categoryTechnicalName: string,
    subCategoryTechnicalName: string,
    currentLocale?: string | null,
  ) => string;
  isLoaded: boolean;
}

function createCategoriesStore(): CategoriesStore {
  const { subscribe, set } = writable<Category[]>([]);

  let categories: Category[] = [];
  let loaded = false;
  let loadPromise: Promise<void> | null = null;

  return {
    subscribe,

    async load(): Promise<void> {
      if (loaded) return;

      // If already loading, wait for the same promise
      if (loadPromise) return loadPromise;

      loadPromise = (async () => {
        try {
          const data = await api.documents.getCategories();
          categories = data;
          set(data);
          loaded = true;
        } catch (error) {
          console.error('Failed to load categories:', error);
          // 403 (EmailWhitelist) means the account isn't provisioned — surface it so
          // App.svelte can show the dedicated access-denied page (Spec 015 E5).
          if (error instanceof ApiError && error.status === 403) throw error;
        }
      })();

      return loadPromise;
    },

    async ensureLoaded(): Promise<void> {
      return this.load();
    },

    /**
     * Get localized category name based on current locale
     * Falls back to German name if English not available
     * @param categoryTechnicalName - The technical name (e.g., "housing-property")
     * @param passedLocale - Optional locale override (pass $locale from templates for reactivity)
     */
    getLocalizedCategoryName(categoryTechnicalName: string, passedLocale?: string | null): string {
      const category = categories.find((c) => c.name === categoryTechnicalName);
      if (!category) return categoryTechnicalName;

      const currentLocale = passedLocale ?? get(locale);

      // Client-side locale selection wins; server localizedName is ignored so a
      // DE/EN UI toggle takes effect without a reload (#205).
      if (currentLocale?.startsWith('en')) {
        return category.displayNameEn ?? category.displayName;
      }
      return category.displayName;
    },

    /**
     * Get localized subcategory name based on current locale
     * Falls back to German name if English not available
     * @param passedLocale - Optional locale override (pass $locale from templates for reactivity)
     */
    getLocalizedSubCategoryName(
      categoryTechnicalName: string,
      subCategoryTechnicalName: string,
      passedLocale?: string | null,
    ): string {
      const category = categories.find((c) => c.name === categoryTechnicalName);
      if (!category) return subCategoryTechnicalName;

      const subCategory = category.subCategories.find((sc) => sc.name === subCategoryTechnicalName);
      if (!subCategory) return subCategoryTechnicalName;

      const currentLocale = passedLocale ?? get(locale);

      // Client-side locale selection wins; server localizedName is ignored (#205).
      if (currentLocale?.startsWith('en')) {
        return subCategory.displayNameEn ?? subCategory.displayName;
      }
      return subCategory.displayName;
    },

    get isLoaded() {
      return loaded;
    },
  };
}

export const categoriesStore = createCategoriesStore();
